import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, TrendingUp, Star, Heart, MessageSquare, Bookmark, Share2, CheckCircle, Filter, Search, List, X, Bookmark as BookmarkIcon, BookmarkCheck, Image, List as ListIcon, Image as ImageIcon } from 'lucide-react';

interface CommunityHighlight {
  id: string;
  text: string;
  context: string;
  position: {
    startOffset: number;
    endOffset: number;
    xpath: string;
  };
  author: {
    id: string;
    name: string;
    avatar?: string;
    role: 'author' | 'moderator' | 'member';
    isVerified: boolean;
    reputation: number;
  };
  category: 'insight' | 'question' | 'discussion' | 'correction' | 'appreciation';
  tags: string[];
  likes: number;
  isLiked: boolean;
  comments: number;
  shares: number;
  views: number;
  isBookmarked: boolean;
  isPinned: boolean;
  isTrending: boolean;
  createdAt: Date;
  updatedAt: Date;
  reactions: {
    emoji: string;
    count: number;
    users: string[];
  }[];
  relatedHighlights: string[];
  communityScore: number;
  trendingScore: number;
  qualityScore: number;
}

interface CommunityInsight {
  id: string;
  title: string;
  description: string;
  type: 'trending' | 'popular' | 'controversial' | 'insightful';
  highlights: string[];
  participants: number;
  engagement: number;
  createdAt: Date;
  tags: string[];
  isActive: boolean;
}

interface CommunityHighlightsProps {
  articleId: string;
  onHighlightClick?: (highlight: CommunityHighlight) => void;
  onHighlightLike?: (highlightId: string, liked: boolean) => void;
  onHighlightBookmark?: (highlightId: string, bookmarked: boolean) => void;
  onHighlightShare?: (highlight: CommunityHighlight) => void;
  className?: string;
}

const CommunityHighlights: React.FC<CommunityHighlightsProps> = ({
  articleId,
  onHighlightClick,
  onHighlightLike,
  onHighlightBookmark,
  onHighlightShare,
  className = ''
}) => {
  const [highlights, setHighlights] = useState<CommunityHighlight[]>([]);
  const [insights, setInsights] = useState<CommunityInsight[]>([]);
  const [showHighlights, setShowHighlights] = useState(true);
  const [showInsights, setShowInsights] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'trending' | 'popular' | 'recent' | 'quality'>('trending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInsight, setSelectedInsight] = useState<CommunityInsight | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock data
  useEffect(() => {
    const mockHighlights: CommunityHighlight[] = [
      {
        id: '1',
        text: 'React Server Components represent one of the most significant shifts in how we think about React applications',
        context: 'This is a fundamental change that affects how we build React apps',
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: '/html/body/div[1]/p[1]'
        },
        author: {
          id: 'user1',
          name: 'Sarah Chen',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
          role: 'member',
          isVerified: true,
          reputation: 1250
        },
        category: 'insight',
        tags: ['react', 'server-components', 'architecture'],
        likes: 45,
        isLiked: false,
        comments: 12,
        shares: 8,
        views: 234,
        isBookmarked: false,
        isPinned: true,
        isTrending: true,
        createdAt: new Date('2024-01-20T10:30:00Z'),
        updatedAt: new Date('2024-01-20T10:30:00Z'),
        reactions: [
          { emoji: '👍', count: 32, users: [] },
          { emoji: '❤️', count: 13, users: [] }
        ],
        relatedHighlights: ['2', '3'],
        communityScore: 0.92,
        trendingScore: 0.88,
        qualityScore: 0.95
      },
      {
        id: '2',
        text: 'Unlike traditional client-side components, RSCs run on the server',
        context: 'This is the key difference that enables better performance',
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: '/html/body/div[1]/p[2]'
        },
        author: {
          id: 'user2',
          name: 'Mike Johnson',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
          role: 'moderator',
          isVerified: true,
          reputation: 2100
        },
        category: 'discussion',
        tags: ['performance', 'server-side', 'react'],
        likes: 28,
        isLiked: true,
        comments: 7,
        shares: 4,
        views: 156,
        isBookmarked: true,
        isPinned: false,
        isTrending: false,
        createdAt: new Date('2024-01-20T11:15:00Z'),
        updatedAt: new Date('2024-01-20T11:15:00Z'),
        reactions: [
          { emoji: '💡', count: 15, users: [] },
          { emoji: '🚀', count: 8, users: [] }
        ],
        relatedHighlights: ['1', '4'],
        communityScore: 0.85,
        trendingScore: 0.72,
        qualityScore: 0.88
      },
      {
        id: '3',
        text: 'This opens up new possibilities for computationally intensive applications',
        context: 'WebAssembly enables high-performance web applications',
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: '/html/body/div[1]/p[3]'
        },
        author: {
          id: 'user3',
          name: 'Alex Rodriguez',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face',
          role: 'member',
          isVerified: false,
          reputation: 890
        },
        category: 'question',
        tags: ['webassembly', 'performance', 'web-development'],
        likes: 15,
        isLiked: false,
        comments: 23,
        shares: 2,
        views: 98,
        isBookmarked: false,
        isPinned: false,
        isTrending: true,
        createdAt: new Date('2024-01-20T14:20:00Z'),
        updatedAt: new Date('2024-01-20T14:20:00Z'),
        reactions: [
          { emoji: '🤔', count: 12, users: [] },
          { emoji: '💭', count: 6, users: [] }
        ],
        relatedHighlights: ['1', '2'],
        communityScore: 0.78,
        trendingScore: 0.85,
        qualityScore: 0.82
      }
    ];

    const mockInsights: CommunityInsight[] = [
      {
        id: '1',
        title: 'React Server Components Discussion',
        description: 'Community is actively discussing the implications of RSCs on web development',
        type: 'trending',
        highlights: ['1', '2'],
        participants: 45,
        engagement: 0.92,
        createdAt: new Date('2024-01-20T10:00:00Z'),
        tags: ['react', 'server-components', 'discussion'],
        isActive: true
      },
      {
        id: '2',
        title: 'Performance Optimization Insights',
        description: 'Multiple community members sharing performance optimization techniques',
        type: 'popular',
        highlights: ['2', '3'],
        participants: 32,
        engagement: 0.88,
        createdAt: new Date('2024-01-20T11:00:00Z'),
        tags: ['performance', 'optimization', 'web-development'],
        isActive: true
      },
      {
        id: '3',
        title: 'WebAssembly Questions',
        description: 'Community asking questions about WebAssembly implementation and use cases',
        type: 'controversial',
        highlights: ['3'],
        participants: 28,
        engagement: 0.75,
        createdAt: new Date('2024-01-20T14:00:00Z'),
        tags: ['webassembly', 'questions', 'controversial'],
        isActive: true
      }
    ];

    setHighlights(mockHighlights);
    setInsights(mockInsights);
  }, [articleId]);

  // Handle highlight actions
  const handleLike = (highlightId: string, liked: boolean) => {
    setHighlights(prev => prev.map(highlight => 
      highlight.id === highlightId 
        ? { ...highlight, isLiked: liked, likes: liked ? highlight.likes + 1 : highlight.likes - 1 }
        : highlight
    ));
    onHighlightLike?.(highlightId, liked);
  };

  const handleBookmark = (highlightId: string, bookmarked: boolean) => {
    setHighlights(prev => prev.map(highlight => 
      highlight.id === highlightId 
        ? { ...highlight, isBookmarked: bookmarked }
        : highlight
    ));
    onHighlightBookmark?.(highlightId, bookmarked);
  };

  const handleShare = (highlight: CommunityHighlight) => {
    onHighlightShare?.(highlight);
  };

  // Filter and sort highlights
  const filteredHighlights = highlights
    .filter(highlight => {
      const matchesCategory = filterCategory === 'all' || highlight.category === filterCategory;
      const matchesSearch = searchQuery === '' || 
        highlight.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        highlight.context.toLowerCase().includes(searchQuery.toLowerCase()) ||
        highlight.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'trending':
          return b.trendingScore - a.trendingScore;
        case 'popular':
          return b.likes - a.likes;
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'quality':
          return b.qualityScore - a.qualityScore;
        default:
          return 0;
      }
    });

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'insight': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      case 'question': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400';
      case 'discussion': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'correction': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400';
      case 'appreciation': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  // Get insight type color
  const getInsightTypeColor = (type: string) => {
    switch (type) {
      case 'trending': return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-400';
      case 'popular': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'controversial': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400';
      case 'insightful': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  // Format date
  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`community-highlights ${className}`}>
      {/* Community Highlights Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Community Highlights
            </h3>
            <button
              onClick={() => setShowHighlights(!showHighlights)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All Categories</option>
              <option value="insight">Insights</option>
              <option value="question">Questions</option>
              <option value="discussion">Discussions</option>
              <option value="correction">Corrections</option>
              <option value="appreciation">Appreciation</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="trending">Trending</option>
              <option value="popular">Popular</option>
              <option value="recent">Recent</option>
              <option value="quality">Quality</option>
            </select>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search highlights..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Community Insights */}
          {showInsights && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4" />
                Community Insights
              </h4>
              
              <div className="space-y-3">
                {insights.map(insight => (
                  <div
                    key={insight.id}
                    className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    onClick={() => setSelectedInsight(insight)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                        {insight.title}
                      </h5>
                      <span className={`px-2 py-1 rounded-full text-xs ${getInsightTypeColor(insight.type)}`}>
                        {insight.type}
                      </span>
                    </div>
                    
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {insight.description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{insight.participants} participants</span>
                      <span>{Math.round(insight.engagement * 100)}% engagement</span>
                      <span>{formatDate(insight.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Community Highlights */}
          {showHighlights && (
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <Star className="w-4 h-4" />
                Popular Highlights ({filteredHighlights.length})
              </h4>
              
              <div className="space-y-4">
                {filteredHighlights.map(highlight => (
                  <div
                    key={highlight.id}
                    className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                      highlight.isPinned 
                        ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20' 
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => onHighlightClick?.(highlight)}
                  >
                    <div className="flex items-start gap-3">
                      <img
                        src={highlight.author.avatar}
                        alt={highlight.author.name}
                        className="w-8 h-8 rounded-full"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-900 dark:text-white text-sm">
                            {highlight.author.name}
                          </span>
                          {highlight.author.isVerified && (
                            <CheckCircle className="w-4 h-4 text-blue-500" />
                          )}
                          <span className="text-xs text-gray-500">
                            {highlight.author.reputation} rep
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(highlight.createdAt)}
                          </span>
                          {highlight.isPinned && (
                            <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 rounded-full">
                              Pinned
                            </span>
                          )}
                          {highlight.isTrending && (
                            <span className="text-xs px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400 rounded-full">
                              Trending
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-900 dark:text-white text-sm mb-2">
                          "{highlight.text}"
                        </p>
                        
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          {highlight.context}
                        </p>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${getCategoryColor(highlight.category)}`}>
                            {highlight.category}
                          </span>
                          {highlight.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                        
                        {/* Reactions */}
                        {highlight.reactions.length > 0 && (
                          <div className="flex items-center gap-2 mb-3">
                            {highlight.reactions.map((reaction, index) => (
                              <button
                                key={index}
                                className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              >
                                <span>{reaction.emoji}</span>
                                <span>{reaction.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLike(highlight.id, !highlight.isLiked);
                            }}
                            className={`flex items-center gap-1 hover:text-red-500 transition-colors ${
                              highlight.isLiked ? 'text-red-500' : ''
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${highlight.isLiked ? 'fill-current' : ''}`} />
                            <span>{highlight.likes}</span>
                          </button>
                          
                          <button className="flex items-center gap-1 hover:text-blue-500 transition-colors">
                            <MessageSquare className="w-4 h-4" />
                            <span>{highlight.comments}</span>
                          </button>
                          
                          <button className="flex items-center gap-1 hover:text-green-500 transition-colors">
                            <Share2 className="w-4 h-4" />
                            <span>{highlight.shares}</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookmark(highlight.id, !highlight.isBookmarked);
                            }}
                            className={`flex items-center gap-1 hover:text-yellow-500 transition-colors ${
                              highlight.isBookmarked ? 'text-yellow-500' : ''
                            }`}
                          >
                            {highlight.isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                          </button>
                        </div>
                        
                        {/* Quality Scores */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Quality: {Math.round(highlight.qualityScore * 100)}%</span>
                          <span>Community: {Math.round(highlight.communityScore * 100)}%</span>
                          <span>Trending: {Math.round(highlight.trendingScore * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {filteredHighlights.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No community highlights found</p>
                    <p className="text-sm">Community highlights will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Insight Details Modal */}
      {selectedInsight && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedInsight.title}
              </h3>
              <button
                onClick={() => setSelectedInsight(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Description</h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {selectedInsight.description}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedInsight.participants}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Participants</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {Math.round(selectedInsight.engagement * 100)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Engagement</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedInsight.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityHighlights;
